import React, { useEffect, useState, useCallback } from 'react';
import {
  Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Divider, Tooltip, Box, Collapse, useMediaQuery, useTheme, Chip, Avatar, Button,
  Snackbar, Alert, Menu, MenuItem
} from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuIcon from '@mui/icons-material/Menu';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArchiveIcon from '@mui/icons-material/Archive';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import ForkliftIcon from '@mui/icons-material/Forklift';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import AddIcon from '@mui/icons-material/Add';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import api from '../api/api';
import AddTruckDialog from './AddTruckDialog';
import { NavLink, useLocation } from 'react-router-dom';
import { getFavoriteTruckIds, getRecentTruckIds, isFavoriteTruck, recordTruckRecent, toggleFavoriteTruck } from '../utils/truckPrefs';
import { VEHICLE_CATEGORIES, getVehicleCategoryId } from '../utils/vehicleCategories';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../auth/AuthContext';
import { MODULES } from '../auth/roles';
import BrandLogo from './BrandLogo';

const drawerWidth = 320;
const collapsedWidth = 70;
const sidebarWidthStorageKey = 'biofleet.sidebarWidth.v1';
const minDrawerWidth = 280;
const maxDrawerWidth = 460;

function fieldMatchesVehicleSearch(value, query) {
  if (!query) return true;
  if (value === null || value === undefined) return false;

  const text = String(value).toLowerCase();
  const unitCodeSearch = /^[a-z]+\d+$/i.test(query);
  if (!unitCodeSearch) return text.includes(query);

  const tokens = text.match(/[a-z0-9]+/g) || [];
  return text === query || tokens.includes(query);
}

export default function Sidebar({ open, setOpen }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const location = useLocation();
  const { user, hasModuleAccess, roleLabel, canAccessPath, logout } = useAuth();

  // Light, navy/blue palette to match the rest of the app
  const palette = {
    bgTop: '#ffffff',
    bgBottom: '#f5f8ff',
    surface: '#ffffff',
    surfaceHover: '#f2f7ff',
    border: 'rgba(0, 31, 63, 0.12)',
    text: '#001f3f',
    muted: 'rgba(0, 31, 63, 0.60)',
    accent: '#1976d2',
    accentDark: '#0f5aa6',
    warn: '#f9a825',
    info: '#1976d2',
  };

  const scrollSx = {
    '&::-webkit-scrollbar': { width: 8 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 31, 63, 0.18)',
      borderRadius: 10,
    },
    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0, 31, 63, 0.28)' },
  };

  const navItemSx = (active, item) => ({
    minHeight: 50,
    justifyContent: open ? 'initial' : 'center',
    px: 2.0,
    py: 1.25,
    borderRadius: 2,
    color: active ? item.color : palette.text,
    position: 'relative',
    transition: 'background-color 180ms ease, border-color 180ms ease, transform 180ms ease',
    background: active ? item.activeBg : 'transparent',
    border: active ? `1px solid ${item.borderColor}` : '1px solid transparent',
    boxShadow: active ? '0 10px 22px rgba(0, 31, 63, 0.10)' : 'none',
    '&::before': active ? {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 4,
      borderRadius: 4,
      background: item.color,
    } : {},
    '&:hover': {
      backgroundColor: item.hoverBg,
      borderColor: active ? item.borderColor : palette.border,
      transform: active ? 'translateX(0px)' : 'translateX(2px)',
    },
  });

  const [vehicles, setVehicles] = useState([]);
  const [vehicleReminderSummary, setVehicleReminderSummary] = useState({});
  const [vehicleOpen, setVehicleOpen] = useState(true);
  const [favoritesOpen, setFavoritesOpen] = useState(true);
  const [recentsOpen, setRecentsOpen] = useState(true);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [addTruckOpen, setAddTruckOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleCatsOpen, setVehicleCatsOpen] = useState({
    fiveTonnes: true,
    midSizeBox: true,
    vans: true,
    trailers: false,
    tractors: false,
    forklifts: false,
  });
  const [prefsTick, setPrefsTick] = useState(0);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [confirm, setConfirm] = useState({ open: false, truck: null });
  const [archiving, setArchiving] = useState(false);
  const [moveMenu, setMoveMenu] = useState({ anchorEl: null, truck: null });
  const [drawerSize, setDrawerSize] = useState(() => {
    const saved = Number(localStorage.getItem(sidebarWidthStorageKey));
    return Number.isFinite(saved) ? Math.min(maxDrawerWidth, Math.max(minDrawerWidth, saved)) : drawerWidth;
  });
  const [resizing, setResizing] = useState(false);

  const vehicleCategoryIcons = {
    fiveTonnes: <FireTruckIcon fontSize="small" />,
    midSizeBox: <LocalShippingIcon fontSize="small" />,
    vans: <AirportShuttleIcon fontSize="small" />,
    trailers: <RvHookupIcon fontSize="small" />,
    tractors: <AgricultureIcon fontSize="small" />,
    forklifts: <ForkliftIcon fontSize="small" />,
  };

  const vehicleCategories = VEHICLE_CATEGORIES.map((c) => ({
    ...c,
    icon: vehicleCategoryIcons[c.id] || <LocalShippingIcon fontSize="small" />,
  }));

  const menuItems = [
    { text: 'Dashboard', icon: <AssessmentIcon />, to: '/dashboard', color: '#2563eb', activeBg: 'linear-gradient(135deg, #edf4ff 0%, #f6f9ff 100%)', hoverBg: '#f3f7ff', borderColor: '#bdd3ff' },
    { text: 'Vehicles', icon: <LocalShippingIcon />, to: '/trucks', color: '#0f8b8d', activeBg: 'linear-gradient(135deg, #eaf8f7 0%, #f5fbfb 100%)', hoverBg: '#f0f9f8', borderColor: '#b7e3df' },
    { text: 'Live GPS', icon: <MyLocationIcon />, to: '/live-gps', color: '#00897b', activeBg: 'linear-gradient(135deg, #e9fbf6 0%, #f6fffc 100%)', hoverBg: '#f0fffb', borderColor: '#b9eadf' },
    { text: 'Truck Scheduling', icon: <EventAvailableIcon />, to: '/truck-scheduling', color: '#246a73', activeBg: 'linear-gradient(135deg, #eaf5f4 0%, #f7fbfb 100%)', hoverBg: '#f0f8f7', borderColor: '#b8d8d5' },
    { text: 'Parts', icon: <Inventory2Icon />, to: '/parts', color: '#7c3aed', activeBg: 'linear-gradient(135deg, #f2edff 0%, #faf8ff 100%)', hoverBg: '#f7f3ff', borderColor: '#d7c5ff' },
    { text: 'Inventory Transfer', icon: <HistoryIcon />, to: '/movements', color: '#b7791f', activeBg: 'linear-gradient(135deg, #fff7e8 0%, #fffaf2 100%)', hoverBg: '#fff9ef', borderColor: '#f2d6a4' },
    { text: 'Inventory Management', icon: <Inventory2Icon />, to: '/inventory', color: '#2e7d32', activeBg: 'linear-gradient(135deg, #edf8ee 0%, #f7fcf7 100%)', hoverBg: '#f2faf3', borderColor: '#bfe0c1' },
    { text: 'Maintenance Reminders', icon: <WarningAmberIcon />, to: '/maintenance-reminders', color: '#dd6b20', activeBg: 'linear-gradient(135deg, #fff2e8 0%, #fff9f5 100%)', hoverBg: '#fff6f0', borderColor: '#f4c8aa' },
    { text: 'Service Logs', icon: <BuildCircleIcon />, to: '/service-logs', color: '#4f46e5', activeBg: 'linear-gradient(135deg, #eef0ff 0%, #f8f8ff 100%)', hoverBg: '#f3f3ff', borderColor: '#c9c7f8' },
    { text: 'Defect Checklist', icon: <FactCheckOutlinedIcon />, to: '/mechanic-defects', color: '#18734b', activeBg: 'linear-gradient(135deg, #e8f7ef 0%, #f7fcf9 100%)', hoverBg: '#f0faf5', borderColor: '#b9dfca' },
    { text: 'Resolved Defects', icon: <TaskAltIcon />, to: '/resolved-defects', color: '#356d59', activeBg: 'linear-gradient(135deg, #e4f2eb 0%, #f7fcf9 100%)', hoverBg: '#eff8f3', borderColor: '#b8d7c8' },
    { text: 'Driver Check-In', icon: <PeopleAltIcon />, to: '/drivers', color: '#c2416c', activeBg: 'linear-gradient(135deg, #fff0f5 0%, #fff8fa 100%)', hoverBg: '#fff4f7', borderColor: '#efbfd0' },
    { text: 'Users', icon: <ManageAccountsIcon />, to: '/users', color: '#087f8c', activeBg: 'linear-gradient(135deg, #eaf8fa 0%, #f6fcfd 100%)', hoverBg: '#f0fafb', borderColor: '#b9e2e7' },
  ].filter((item) => canAccessPath(item.to));

  const handleNavigate = () => {
    if (isMobile) setOpen(false);
  };

  useEffect(() => {
    if (!resizing) return undefined;

    const onMouseMove = (event) => {
      const next = Math.min(maxDrawerWidth, Math.max(minDrawerWidth, event.clientX));
      setDrawerSize(next);
    };
    const onMouseUp = () => {
      setResizing(false);
      localStorage.setItem(sidebarWidthStorageKey, String(drawerSize));
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [drawerSize, resizing]);

  const isDashboardActive = () =>
    location.pathname === '/' || location.pathname === '/dashboard';

  const fetchVehicles = useCallback(async () => {
    if (!hasModuleAccess(MODULES.VEHICLES)) {
      setVehicles([]);
      setVehicleReminderSummary({});
      return;
    }

    try {

      const [res, reminderRes] = await Promise.all([
        api.get('/trucks?includeArchived=1'),
        api.get('/consumables/reminders/summary').catch(() => ({ data: {} })),
      ]);

      const data = res.data?.data || res.data || [];

      setVehicles(Array.isArray(data) ? data : []);
      setVehicleReminderSummary(reminderRes.data || {});

    } catch (err) {
      console.error(err);
    }
  }, [hasModuleAccess]);

  // ✅ SINGLE effect only
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // ✅ Refresh after Excel upload (if triggered)
  useEffect(() => {
    const handler = () => fetchVehicles();
    window.addEventListener('vehiclesUpdated', handler);
    window.addEventListener('dataUpdated', handler);
    return () => {
      window.removeEventListener('vehiclesUpdated', handler);
      window.removeEventListener('dataUpdated', handler);
    };
  }, [fetchVehicles]);

  useEffect(() => {
    const handler = () => setPrefsTick((v) => v + 1);
    window.addEventListener('prefsUpdated', handler);
    return () => window.removeEventListener('prefsUpdated', handler);
  }, []);

  const handleTruckAdded = async () => {
    await fetchVehicles();
    window.dispatchEvent(new Event('vehiclesUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));
  };

  const requestArchive = (truck) => setConfirm({ open: true, truck });

  const handleConfirmArchive = async () => {
    if (!confirm.truck?.id) return;
    try {
      setArchiving(true);
      const res = await api.put(`/trucks/${confirm.truck.id}/archive`);
      const archivedPartCount = Number(res.data?.archived_parts_count || 0);
      await fetchVehicles();
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      const truckLabel = confirm.truck.name || `Truck #${confirm.truck.id}`;
      setToast({
        open: true,
        severity: 'success',
        message: archivedPartCount
          ? `Archived ${truckLabel} and ${archivedPartCount} attached part record${archivedPartCount === 1 ? '' : 's'}.`
          : `Archived ${truckLabel}.`,
      });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Archive failed';
      setToast({ open: true, severity: 'error', message });
    } finally {
      setArchiving(false);
      setConfirm({ open: false, truck: null });
    }
  };

  const handleToggleFavoriteWithToast = (truckId) => {
    const wasFavorite = isFavoriteTruck(truckId);
    toggleFavoriteTruck(truckId);
    setToast({ open: true, severity: 'success', message: wasFavorite ? 'Removed from favorites.' : 'Added to favorites.' });
  };

  const openMoveMenu = (e, truck) => {
    e.preventDefault();
    e.stopPropagation();
    setMoveMenu({ anchorEl: e.currentTarget, truck });
  };

  const closeMoveMenu = () => setMoveMenu({ anchorEl: null, truck: null });

  const moveVehicleToCategory = async (categoryId) => {
    const t = moveMenu.truck;
    if (!t?.id) return;
    try {
      await api.put(`/trucks/${t.id}`, { category: categoryId || null });
      await fetchVehicles();
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      const label = vehicleCategories.find((c) => c.id === categoryId)?.label || 'Auto (detect)';
      setToast({ open: true, severity: 'success', message: `Moved to ${label}.` });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Failed to move vehicle.';
      setToast({ open: true, severity: 'error', message });
    } finally {
      closeMoveMenu();
    }
  };

  const allActive = vehicles.filter((v) => !v.archived);
  const allArchived = vehicles.filter((v) => v.archived);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredActive = allActive.filter((v) => {
    const searchableValues = [
      v.name,
      v.make,
      v.model,
      v.license_plate,
      v.plate,
      v.serial_number,
      v.vin,
      v.status,
      v.id,
    ];

    const matchesSearch = !normalizedSearch || searchableValues.some((value) => fieldMatchesVehicleSearch(value, normalizedSearch));
    const s = String(v.status || '').toLowerCase();
    const matchesStatus =
      statusFilter === 'all'
      || (statusFilter === 'active' && s === 'active')
      || (statusFilter === 'maintenance' && s === 'maintenance')
      || (statusFilter === 'inactive' && s === 'inactive')
      || (statusFilter === 'out of service' && s === 'out of service');

    return matchesSearch && matchesStatus;
  });

  const favoriteIds = getFavoriteTruckIds();
  const favorites = filteredActive.filter((v) => favoriteIds.includes(v.id));
  const recentIds = getRecentTruckIds(8);
  const recents = recentIds.map((id) => filteredActive.find((v) => v.id === id)).filter(Boolean);

  const renderReminderBadge = (vehicle) => {
    const summary = vehicleReminderSummary[String(vehicle?.id)] || null;
    if (!summary?.total) return null;
    const isRed = Number(summary.red || 0) > 0;
    const label = summary.total > 9 ? '9+' : String(summary.total);
    return (
      <Tooltip
        title={`${summary.total} scheduled maintenance reminder${summary.total === 1 ? '' : 's'}${isRed ? ' — overdue item present' : ''}`}
        placement="top"
      >
        <Chip
          size="small"
          icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
          label={label}
          sx={{
            height: 22,
            minWidth: 34,
            bgcolor: isRed ? '#ffebee' : '#fff8e1',
            color: isRed ? '#c62828' : '#f57c00',
            border: `1px solid ${isRed ? '#ef9a9a' : '#ffcc80'}`,
            fontWeight: 900,
            '& .MuiChip-icon': { color: isRed ? '#c62828' : '#f57c00', ml: 0.4 },
            '& .MuiChip-label': { px: 0.6 },
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </Tooltip>
    );
  };

  const vehiclesByCategory = React.useMemo(() => {
    const groups = Object.fromEntries(vehicleCategories.map((c) => [c.id, []]));
    filteredActive.forEach((t) => {
      const cid = getVehicleCategoryId(t);
      if (!groups[cid]) groups[cid] = [];
      groups[cid].push(t);
    });
    return groups;
  }, [filteredActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDrawerWidth = isMobile ? drawerWidth : (open ? drawerSize : collapsedWidth);

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={() => isMobile && setOpen(false)}
      sx={{
        width: isMobile ? 0 : activeDrawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          position: isMobile ? 'fixed' : 'relative',
          width: activeDrawerWidth,
          transition: resizing ? 'none' : 'width 0.3s ease-in-out',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: `linear-gradient(180deg, ${palette.bgTop} 0%, ${palette.bgBottom} 100%)`,
          color: palette.text,
          boxShadow: '6px 0 18px rgba(0, 31, 63, 0.10)',
          borderRight: `1px solid ${palette.border}`,
        },
      }}
    >
      {!isMobile && open && (
        <Tooltip title="Drag to resize sidebar" placement="right">
          <Box
            onMouseDown={(event) => {
              event.preventDefault();
              setResizing(true);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              right: -4,
              width: 8,
              height: '100%',
              zIndex: 20,
              cursor: 'col-resize',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '45%',
                right: 2,
                width: 3,
                height: 48,
                borderRadius: 99,
                bgcolor: resizing ? palette.accent : 'rgba(25,118,210,0.30)',
              },
              '&:hover::after': {
                bgcolor: palette.accent,
              },
            }}
          />
        </Tooltip>
      )}
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: open ? 'row' : 'column',
          justifyContent: 'center',
          gap: open ? 0 : 1.25,
          p: open ? 2.5 : 2,
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: open ? 1.5 : 0, minWidth: 0 }}>
          <BrandLogo
            height={open ? 50 : 42}
            compact={!open}
            showText={open}
            textColor="#ffffff"
            subtitleColor="rgba(255,255,255,0.72)"
            logoGlow
            sx={{ minWidth: 0 }}
          />
        </Box>
        <IconButton
          onClick={() => setOpen(!open)}
          sx={{
            color: '#ffffff',
            backgroundColor: 'rgba(255,255,255,0.14)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.22)',
              transform: 'scale(1.03)',
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      <Divider sx={{ backgroundColor: palette.border }} />

      <Box sx={{ flex: 1, overflowY: 'auto', ...scrollSx }}>
        {/* Main menu */}
        <List sx={{ px: 1.5, py: 2 }}>
          {open && (
            <Box sx={{ px: 1, pb: 1.25 }}>
              <Box sx={{ fontSize: 11, letterSpacing: 1.2, color: palette.muted, textTransform: 'uppercase' }}>
                Navigation
              </Box>
            </Box>
          )}
        {menuItems.map((item) => (
          <Tooltip key={item.text} title={!open ? item.text : ''} placement="right">
            <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
              <ListItemButton
                component={NavLink}
                to={item.to}
                onClick={handleNavigate}
                sx={navItemSx(item.text === 'Dashboard' ? isDashboardActive() : location.pathname === item.to, item)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 1.75 : 'auto',
                    justifyContent: 'center',
                    color: item.color,
                    fontSize: 24,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: (item.text === 'Dashboard' ? isDashboardActive() : location.pathname === item.to) ? 800 : 600,
                        color: (item.text === 'Dashboard' ? isDashboardActive() : location.pathname === item.to) ? item.color : palette.text,
                        fontSize: 15
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </Tooltip>
        ))}

        {hasModuleAccess(MODULES.VEHICLES) && (
          <>
        {/* Vehicles Section */}
        <Divider sx={{ my: 2, backgroundColor: palette.border }} />

        {open && (
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search trucks..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.muted }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: palette.text,
                  '& fieldset': { borderColor: palette.border },
                  '&:hover fieldset': { borderColor: 'rgba(25,118,210,0.55)' },
                  '&.Mui-focused fieldset': { borderColor: palette.accent },
                },
                '& .MuiInputBase-input::placeholder': { color: palette.muted },
              }}
            />
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['all', 'active', 'maintenance', 'inactive', 'out of service'].map((k) => (
                <Chip
                  key={k}
                  size="small"
                  clickable
                  onClick={() => setStatusFilter(k)}
                  label={k === 'all' ? 'All' : k.toUpperCase()}
                  sx={{
                    bgcolor: statusFilter === k ? 'rgba(25,118,210,0.16)' : palette.surface,
                    color: statusFilter === k ? palette.accent : palette.text,
                    border: statusFilter === k ? '1px solid rgba(25,118,210,0.30)' : `1px solid ${palette.border}`,
                    fontWeight: 700,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Favorites */}
        <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
          <ListItemButton
            onClick={() => setFavoritesOpen(!favoritesOpen)}
            sx={{
              minHeight: 52,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              background: favoritesOpen ? palette.surface : 'transparent',
              '&:hover': { backgroundColor: palette.surfaceHover },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center', color: palette.warn }}>
              <StarIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Favorites" primaryTypographyProps={{ sx: { fontWeight: 800, color: palette.warn } }} />}
            {open && <Chip label={favorites.length} size="small" sx={{ bgcolor: 'rgba(251,191,36,0.18)', color: palette.warn, fontWeight: 800 }} />}
            {open && (favoritesOpen ? <ExpandLess sx={{ color: palette.warn }} /> : <ExpandMore sx={{ color: palette.muted }} />)}
          </ListItemButton>
          <Collapse in={favoritesOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ mt: 0.5, px: open ? 1 : 0 }}>
              {favorites.length === 0 && open && (
                <Box sx={{ px: 2.5, py: 1, color: palette.muted, fontSize: 12 }}>
                  Star a truck to pin it here.
                </Box>
              )}
              {favorites.map((v) => (
                <Tooltip key={`fav-${v.id}-${prefsTick}`} title={!open ? v.name : ''} placement="right">
                  <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                    <ListItemButton
                      component={NavLink}
                      to={`/truck/${v.id}`}
                      onClick={() => { recordTruckRecent(v.id); handleNavigate(); }}
                      sx={{
                        pl: open ? 4 : 2,
                        pr: 1,
                        py: 1.1,
                        borderRadius: 1.5,
                        justifyContent: 'space-between',
                        color: location.pathname === `/truck/${v.id}` ? palette.accent : palette.text,
                        backgroundColor: location.pathname === `/truck/${v.id}` ? 'rgba(25,118,210,0.10)' : 'transparent',
                        borderLeft: location.pathname === `/truck/${v.id}` ? `3px solid ${palette.accent}` : '3px solid transparent',
                        '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <LocalShippingIcon sx={{ fontSize: 18 }} />
                        {open && (
                          <ListItemText
                            primary={v.name || `${v.make || ''} ${v.model || ''}`.trim()}
                            primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          />
                        )}
                      </Box>
                      {open && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {renderReminderBadge(v)}
                          <IconButton
                            size="small"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleFavoriteWithToast(v.id); }}
                            sx={{ color: palette.warn, '&:hover': { backgroundColor: 'rgba(251,191,36,0.14)' } }}
                          >
                            <StarIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              ))}
            </List>
          </Collapse>
        </ListItem>

        {/* Recents */}
        <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
          <ListItemButton
            onClick={() => setRecentsOpen(!recentsOpen)}
            sx={{
              minHeight: 52,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              background: recentsOpen ? palette.surface : 'transparent',
              '&:hover': { backgroundColor: palette.surfaceHover },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center', color: palette.info }}>
              <HistoryIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Recents" primaryTypographyProps={{ sx: { fontWeight: 800, color: palette.info } }} />}
            {open && <Chip label={recents.length} size="small" sx={{ bgcolor: 'rgba(96,165,250,0.16)', color: palette.info, fontWeight: 800 }} />}
            {open && (recentsOpen ? <ExpandLess sx={{ color: palette.info }} /> : <ExpandMore sx={{ color: palette.muted }} />)}
          </ListItemButton>
          <Collapse in={recentsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ mt: 0.5, px: open ? 1 : 0 }}>
              {recents.length === 0 && open && (
                <Box sx={{ px: 2.5, py: 1, color: palette.muted, fontSize: 12 }}>
                  Recently edited trucks show up here.
                </Box>
              )}
              {recents.map((v) => (
                <Tooltip key={`recent-${v.id}-${prefsTick}`} title={!open ? v.name : ''} placement="right">
                  <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                    <ListItemButton
                      component={NavLink}
                      to={`/truck/${v.id}`}
                      onClick={() => { recordTruckRecent(v.id); handleNavigate(); }}
                      sx={{
                        pl: open ? 4 : 2,
                        pr: 1,
                        py: 1.1,
                        borderRadius: 1.5,
                        justifyContent: 'space-between',
                        color: location.pathname === `/truck/${v.id}` ? palette.accent : palette.text,
                        backgroundColor: location.pathname === `/truck/${v.id}` ? 'rgba(25,118,210,0.10)' : 'transparent',
                        borderLeft: location.pathname === `/truck/${v.id}` ? `3px solid ${palette.accent}` : '3px solid transparent',
                        '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <LocalShippingIcon sx={{ fontSize: 18 }} />
                        {open && (
                          <ListItemText
                            primary={v.name || `${v.make || ''} ${v.model || ''}`.trim()}
                            primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          />
                        )}
                      </Box>
                      {open && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {renderReminderBadge(v)}
                          <IconButton
                            size="small"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleFavoriteWithToast(v.id); }}
                            sx={{ color: isFavoriteTruck(v.id) ? palette.warn : palette.muted, '&:hover': { backgroundColor: 'rgba(251,191,36,0.12)' } }}
                          >
                            {isFavoriteTruck(v.id) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              ))}
            </List>
          </Collapse>
        </ListItem>

        <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
          <ListItemButton
            onClick={() => setVehicleOpen(!vehicleOpen)}
            sx={{
              minHeight: 52,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              background: vehicleOpen
                ? 'linear-gradient(135deg, rgba(25,118,210,0.14) 0%, rgba(25,118,210,0.06) 100%)'
                : 'transparent',
              border: '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(25,118,210,0.10)',
                borderColor: 'rgba(25,118,210,0.22)'
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: vehicleOpen ? palette.accent : palette.text,
              }}
            >
              <DirectionsCarIcon />
            </ListItemIcon>
            {open && (
              <>
                <ListItemText
                  primary="Vehicles"
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: vehicleOpen ? 'bold' : 'medium',
                      color: vehicleOpen ? palette.accent : palette.text,
                      fontSize: 15
                    }
                  }}
                />
                <Chip
                  label={filteredActive.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 800,
                    backgroundColor: 'rgba(25,118,210,0.14)',
                    color: palette.accent,
                    mr: 1
                  }}
                />
                {vehicleOpen ? <ExpandLess sx={{ color: palette.accent }} /> : <ExpandMore sx={{ color: palette.muted }} />}
              </>
            )}
          </ListItemButton>

          <Collapse in={vehicleOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ mt: 1, px: open ? 1 : 0 }}>
              {vehicleCategories.map((cat) => {
                const catVehicles = vehiclesByCategory[cat.id] || [];
                const isCatOpen = !!vehicleCatsOpen[cat.id];

                return (
                  <React.Fragment key={cat.id}>
                    <Tooltip title={!open ? cat.label : ''} placement="right">
                      <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => setVehicleCatsOpen((prev) => ({ ...prev, [cat.id]: !prev[cat.id] }))}
                          sx={{
                            minHeight: 46,
                            justifyContent: open ? 'initial' : 'center',
                            px: 2.5,
                            py: 1.1,
                            borderRadius: 2,
                            background: isCatOpen ? palette.surfaceHover : 'transparent',
                            border: `1px solid ${isCatOpen ? palette.border : 'transparent'}`,
                            '&:hover': { backgroundColor: palette.surfaceHover },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 2 : 'auto',
                              justifyContent: 'center',
                              color: palette.accent,
                            }}
                          >
                            {cat.icon}
                          </ListItemIcon>
                          {open && (
                            <>
                              <ListItemText
                                primary={cat.label}
                                primaryTypographyProps={{ sx: { fontWeight: 800, color: palette.text, fontSize: 13 } }}
                              />
                              <Chip
                                label={catVehicles.length}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  backgroundColor: 'rgba(25,118,210,0.10)',
                                  color: palette.accent,
                                  mr: 1
                                }}
                              />
                              <Tooltip title="Open category page">
                                <IconButton
                                  size="small"
                                  component={NavLink}
                                  to={`/trucks/${cat.slug}`}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    color: palette.muted,
                                    mr: 0.5,
                                    '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)', color: palette.accent },
                                  }}
                                >
                                  <OpenInNewIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              {isCatOpen ? <ExpandLess sx={{ color: palette.muted }} /> : <ExpandMore sx={{ color: palette.muted }} />}
                            </>
                          )}
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>

                    <Collapse in={isCatOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ mt: 0.25, mb: 0.5, px: open ? 1 : 0 }}>
                        {catVehicles.map((v) => (
                          <Tooltip key={`${cat.id}-${v.id}`} title={!open ? (v.name || `${v.make} ${v.model}`) : ''} placement="right">
                            <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                              <ListItemButton
                                component={NavLink}
                                to={`/truck/${v.id}`}
                                onClick={() => { recordTruckRecent(v.id); handleNavigate(); }}
                                sx={{
                                  pl: open ? 4.5 : 2,
                                  pr: 1,
                                  py: 1.15,
                                  borderRadius: 1.5,
                                  justifyContent: 'space-between',
                                  color: location.pathname === `/truck/${v.id}` ? palette.accent : palette.text,
                                  backgroundColor:
                                    location.pathname === `/truck/${v.id}` ? 'rgba(25,118,210,0.10)' : 'transparent',
                                  borderLeft:
                                    location.pathname === `/truck/${v.id}`
                                      ? `3px solid ${palette.accent}`
                                      : '3px solid transparent',
                                  '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                  <LocalShippingIcon sx={{ fontSize: 18, color: palette.muted }} />
                                  {open && (
                                    <ListItemText
                                      primary={v.name || `${v.make} ${v.model}`}
                                      primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                    />
                                  )}
                                </Box>

                                {open && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {renderReminderBadge(v)}
                                    <Tooltip title="Move category">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => openMoveMenu(e, v)}
                                        sx={{
                                          color: palette.muted,
                                          '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)', color: palette.accent },
                                        }}
                                      >
                                        <DriveFileMoveIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggleFavoriteWithToast(v.id);
                                      }}
                                      sx={{
                                        color: isFavoriteTruck(v.id) ? palette.warn : palette.muted,
                                        '&:hover': { backgroundColor: 'rgba(251,158,37,0.14)' },
                                      }}
                                    >
                                      {isFavoriteTruck(v.id) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                    </IconButton>

                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        requestArchive(v);
                                      }}
                                      sx={{
                                        color: palette.muted,
                                        '&:hover': { backgroundColor: 'rgba(0, 31, 63, 0.06)' },
                                      }}
                                    >
                                      <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </ListItemButton>
                            </ListItem>
                          </Tooltip>
                        ))}

                        {catVehicles.length === 0 && open && (
                          <Box sx={{ px: 2.5, py: 1, color: palette.muted, fontSize: 12 }}>
                            No vehicles in this category.
                          </Box>
                        )}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}

              {open && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddTruckOpen(true)}
                    sx={{
                      color: palette.accent,
                      borderColor: 'rgba(25,118,210,0.45)',
                      borderRadius: 2,
                      py: 1,
                      fontSize: 13,
                      fontWeight: 800,
                      '&:hover': {
                        borderColor: palette.accent,
                        backgroundColor: 'rgba(25,118,210,0.08)',
                      }
                    }}
                  >
                    Add Vehicle
                  </Button>
                </Box>
              )}
            </List>
          </Collapse>
        </ListItem>

        {/* Archived */}
        <Divider sx={{ my: 2, backgroundColor: palette.border }} />
        <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
          <ListItemButton
            onClick={() => setArchivedOpen(!archivedOpen)}
            sx={{
              minHeight: 52,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              background: archivedOpen ? palette.surface : 'transparent',
              border: '1px solid transparent',
              '&:hover': {
                backgroundColor: palette.surfaceHover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: archivedOpen ? palette.accent : palette.text,
              }}
            >
              <ArchiveIcon />
            </ListItemIcon>
            {open && (
              <>
                <ListItemText
                  primary="Archived"
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: archivedOpen ? 'bold' : 'medium',
                      color: archivedOpen ? palette.accent : palette.text,
                      fontSize: 15
                    }
                  }}
                />
                <Chip
                  label={allArchived.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 800,
                    backgroundColor: palette.surface,
                    color: palette.text,
                    mr: 1
                  }}
                />
                {archivedOpen ? <ExpandLess sx={{ color: palette.accent }} /> : <ExpandMore sx={{ color: palette.muted }} />}
              </>
            )}
          </ListItemButton>

          <Collapse in={archivedOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ mt: 1, px: open ? 1 : 0 }}>
              {allArchived.map((v) => (
                <Tooltip key={`arch-${v.id}`} title={!open ? v.name : ''} placement="right">
                  <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                    <ListItemButton
                      component={NavLink}
                      to={`/truck/${v.id}`}
                      onClick={() => { recordTruckRecent(v.id); handleNavigate(); }}
                      sx={{
                        pl: open ? 4 : 2,
                        pr: 1,
                        py: 1.25,
                        borderRadius: 1.5,
                        justifyContent: 'space-between',
                        color: location.pathname === `/truck/${v.id}` ? palette.accent : palette.text,
                        backgroundColor:
                          location.pathname === `/truck/${v.id}` ? 'rgba(25,118,210,0.10)' : 'transparent',
                        borderLeft:
                          location.pathname === `/truck/${v.id}`
                            ? `3px solid ${palette.accent}`
                            : '3px solid transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(25,118,210,0.08)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon sx={{ fontSize: 18 }} />
                        {open && (
                          <ListItemText
                            primary={v.name || `${v.make} ${v.model}`}
                            primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500 } }}
                          />
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              ))}

              {allArchived.length === 0 && open && (
                <Box sx={{ px: 2.5, py: 1, color: palette.muted, fontSize: 12 }}>
                  No archived trucks.
                </Box>
              )}
            </List>
          </Collapse>
        </ListItem>
          </>
        )}
      </List>
      </Box>

      <Box sx={{ p: open ? 1.5 : 1, borderTop: `1px solid ${palette.border}` }}>
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, bgcolor: 'rgba(25,118,210,0.06)' }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: '#001f3f', fontSize: 14 }}>
              {(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ fontSize: 13, fontWeight: 900, color: palette.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name || 'User'}
              </Box>
              <Box sx={{ fontSize: 11, color: palette.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {roleLabel}
              </Box>
            </Box>
            <Tooltip title="Logout">
              <IconButton size="small" onClick={logout} sx={{ color: palette.muted }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Tooltip title="Logout" placement="right">
            <IconButton onClick={logout} sx={{ color: palette.text }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <AddTruckDialog
        open={addTruckOpen}
        onClose={() => setAddTruckOpen(false)}
        onTruckAdded={handleTruckAdded}
      />

      <ConfirmDialog
        open={confirm.open}
        title="Archive Truck?"
        description={`This will hide the truck from your primary lists, but it will stay in the database and can be viewed under Archived.`}
        confirmText={archiving ? 'Archiving...' : 'Archive'}
        confirmColor="warning"
        onConfirm={handleConfirmArchive}
        onClose={() => setConfirm({ open: false, truck: null })}
        loading={archiving}
        icon={<ArchiveIcon />}
      />

      <Menu
        anchorEl={moveMenu.anchorEl}
        open={Boolean(moveMenu.anchorEl)}
        onClose={closeMoveMenu}
      >
        <MenuItem onClick={() => moveVehicleToCategory('')}>
          <em>Auto (detect)</em>
        </MenuItem>
        <Divider />
        {vehicleCategories.map((c) => (
          <MenuItem key={c.id} onClick={() => moveVehicleToCategory(c.id)}>
            {c.label}
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
}
